import React, { useState, useEffect } from "react";
import { useForm } from "@mantine/form";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { TextInputField, DateTimeInputField, Button as FormButton } from "../FormInputs";
import { IconCheck } from "@tabler/icons-react";
import StatusModal from "../StatusModal";
import styles from "./AnnouncementModal.module.css";
import announcementImage from "../../assets/images/announcement.jpg";
import { Flex, Text } from "@mantine/core";
import { config } from "../../utils/config";

const MIN_DATE = new Date(2026, 1, 12, 11, 0); // 12 Feb 11 AM
const MAX_DATE = new Date(2026, 1, 14, 17, 0); // 14 Feb 5 PM

const MIN_HOUR = 11;
const MAX_HOUR = 17;

export default function AnnouncementModal() {
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error' or null

  const form = useForm({
    initialValues: {
      name: "",
      contactNumber: "",
      companyName: "",
      designation: "",
      dateTime: new Date(2026, 1, 12, 11, 0),
    },
    validate: {
      name: (value) => (!value ? "Name is required" : null),
      contactNumber: (value) => (!value ? "Contact number is required" : null),
      companyName: (value) => (!value ? "Company name is required" : null),
      dateTime: (value) => (!value ? "Date and time is required" : null),
    },
  });

  useEffect(() => {
    const now = new Date();

    // Do not open modal after max date
    if (now > MAX_DATE) {
      return;
    }

    // ✅ Show modal after 6 seconds
    const timer = setTimeout(() => {
      setOpened(true);
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (name, value) => {
    if (name === "contactNumber") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      form.setFieldValue(name, digitsOnly);
      return;
    }

    if (name === "dateTime" && value instanceof Date) {
      let corrected = new Date(value);

      // ⛔ Clamp date range
      if (corrected < MIN_DATE) {
        form.setFieldValue(name, new Date(MIN_DATE));
        return;
      }

      if (corrected > MAX_DATE) {
        form.setFieldValue(name, new Date(MAX_DATE));
        return;
      }

      // ⏰ Clamp time range (daily)
      const hours = corrected.getHours();
      if (hours < MIN_HOUR || hours > MAX_HOUR) {
        corrected.setHours(MIN_HOUR, 0, 0, 0); // reset to 11 AM
      }

      form.setFieldValue(name, corrected);
      return;
    }

    form.setFieldValue(name, value);
  };

  const handleSubmit = async (values) => {
    console.log("Form submitted:", values);

    setLoading(true);

    try {
      // Format the date and time
      const formattedDateTime = dayjs(values.dateTime).format("DD/MM/YYYY HH:mm");

      // Create a formatted message
      const message =
        `*New Consultation Request*\n\n` +
        `*Name:* ${values.name}\n` +
        `*Contact Number:* ${values.contactNumber}\n` +
        `*Company Name:* ${values.companyName}\n` +
        `*Designation:* ${values.designation || "N/A"}\n` +
        `*Preferred Date & Time:* ${formattedDateTime}`;

      const response = await fetch(`${config.apiUrl}/api/send_slack/`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          channel: config.channelName,
          message: message,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Success:", data);

      setOpened(false);
      setSubmitStatus("success");
    } catch (error) {
      console.error("Error:", error);
      setOpened(false);
      setSubmitStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {opened && (
          <>
            {/* Backdrop */}
            <motion.div
              className={styles.backdrop}
              onClick={() => setOpened(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />

            {/* Modal */}
            <div className={styles.modalWrapper}>
              <motion.div
                className={styles.modal}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {/* Close Button */}
                <button className={styles.closeButton} onClick={() => setOpened(false)} aria-label="Close modal">
                  ✕
                </button>

                {/* Left Section - Image */}
                <div className={styles.leftSection}>
                  <img src={announcementImage} alt="IFEX 2026 Announcement" className={styles.announcementImage} />
                </div>

                {/* Right Section - Form */}
                <div className={styles.rightSection}>
                  <h2 className={styles.formTitle}>Schedule your 1-on-1 expert consultation now!</h2>

                  <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form} noValidate>
                    <TextInputField
                      name="name"
                      label="Name"
                      placeholder="Enter your name here"
                      required
                      error={form.errors.name}
                      value={form.values.name}
                      onChange={handleInputChange}
                    />

                    <TextInputField
                      name="contactNumber"
                      label="Contact Number"
                      placeholder="Enter your contact number"
                      type="tel"
                      required
                      error={form.errors.contactNumber}
                      value={form.values.contactNumber}
                      onChange={handleInputChange}
                    />

                    <TextInputField
                      name="companyName"
                      label="Company Name"
                      placeholder="Enter your Company Name"
                      required
                      error={form.errors.companyName}
                      value={form.values.companyName}
                      onChange={handleInputChange}
                    />

                    <TextInputField
                      name="designation"
                      label="Designation"
                      placeholder="Enter Your Designation"
                      error={form.errors.designation}
                      value={form.values.designation}
                      onChange={handleInputChange}
                    />

                    <DateTimeInputField
                      name="dateTime"
                      label="Book Your Slot"
                      placeholder="Select a Date & Time"
                      required
                      error={form.errors.dateTime}
                      minDate={MIN_DATE} // 12 Feb 2026, 11:00 AM
                      maxDate={MAX_DATE} // 14 Feb 2026, 5:00 PM
                      value={form.values.dateTime}
                      onChange={handleInputChange}
                    />
                    <Text c="dimmed" size="xs">
                      The consultation slots are available from 11:00 AM to 5:00 PM for 12th to 14th February 2026.
                    </Text>
                    <Flex justify={"flex-end"}>
                      <FormButton
                        loading={loading}
                        width="40%"
                        align="right"
                        leftSection={<IconCheck size={22} strokeWidth={3} />}
                        className={styles.submitButton}
                      >
                        Submit
                      </FormButton>
                    </Flex>
                  </form>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Status Modal */}
      <StatusModal opened={submitStatus !== null} onClose={() => setSubmitStatus(null)} status={submitStatus} />
    </>
  );
}
